import React, { useMemo } from "react";
import { parseGeoData } from "senf-shared";
import styled from "styled-components";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import { cn } from "../../../util/classNameConstructor";
import { useCenterScrollZoom } from "../../atoms/map/hooks/useCenterScrollZoom";
import MapViewEditor from "../../atoms/map/MapViewEditor";
import IdeaMapList from "./components/IdeaMapList";

const ScaleContainer = styled.div<{ state: string; transformOrigin: string }>`
  @media (min-width: 768px) {
    width: var(--w-screen,100vw);
    height: 100%;
    margin-top: 0vh;
    z-index: 90;
    top: 0;
    position: fixed;
    scale: ${({ state }) => (state !== "show" ? 0.95 : 1)};
    transform-origin: ${({ transformOrigin }) => transformOrigin} 50%;
    transition-duration: 0.3s;
    transition-property: left, scale, opacity;
    transition-timing-function: ${({ theme }) => theme.easing.expressive};
    pointer-events: none;
    & > * {
      pointer-events: all;
    }
  }
`;

const IdeaMap = ({
  ideaMap,
  pathname,
  handle,
  idea,
  ideaId,
  profileId,
  searchTerm,
  setSearchTerm,
  statefulMap,
  setStatefulMap,
  project,
  projectIdeas,
  filteredProjectIdeas,
  setSelectedArealFilter,
  selectedArealFilter,
  open,
  setOpen,
  setPosition,
  checkedSortOption,
  setCheckedSortOption,
  activeFilters,
  setActiveFilters,
  accessibilityInfotext,
  resizeDependencies,
  isMobile: isMobileProp,
  preview,
}) => {
  const isMobileFallback = useIsMobile();
  const isMobile = isMobileProp ?? isMobileFallback;

  // Create a stable key that only changes when map-relevant properties change
  const filteredProjectIdeasKey = useMemo(() => {
    if (!filteredProjectIdeas) return null;
    return filteredProjectIdeas
      .map(
        (item) =>
          `${item.lat}-${item.long}-${JSON.stringify(item.geoData)}-${item.ideaId}`,
      )
      .join("|");
  }, [filteredProjectIdeas]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoize the filteredProjectIdeas based on map-relevant properties only
  const memoizedFilteredProjectIdeas = useMemo(() => {
    if (!filteredProjectIdeas) return null;

    return filteredProjectIdeas.map((item) => ({
      lat: item.lat,
      long: item.long,
      geoData: item.geoData,
    }));
  }, [filteredProjectIdeasKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoize the idea's map-relevant properties to prevent unnecessary recalculations
  // We only care about lat, long, geoData for map rendering - other properties like likes don't affect the map
  const memoizedIdea = useMemo(() => {
    if (!idea) return null;
    return {
      lat: idea.lat,
      long: idea.long,
      geoData: idea.geoData,
    };
  }, [idea?.lat, idea?.long, idea?.geoData]); // eslint-disable-line react-hooks/exhaustive-deps

  const gisDataArray = useMemo(() => {
    const isAddOrEditPage =
      pathname.indexOf("addIdea") !== -1 || pathname.indexOf("editIdea") !== -1;

    const baseArray = [
      ...(ideaMap?.map?.gisData || []),
      !isAddOrEditPage && {
        type: "FeatureCollection",
        features: filteredProjectIdeas?.flatMap((item) => {
          return (
            item.geoData?.features || {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates:
                  item.height !== undefined
                    ? [item.long, item.lat, item.height]
                    : [item.long, item.lat],
              },
              properties: { ...item },
            }
          );
        }),
      },
    ].filter(Boolean);

    // Include the memoized idea in the array
    if (memoizedIdea) {
      baseArray.push(memoizedIdea);
    }
    return baseArray;
  }, [ideaMap?.map?.gisData, memoizedFilteredProjectIdeas, pathname]);

  useCenterScrollZoom(statefulMap, {
    enabled: ideaId && idea,
  });

  return (
    <React.Fragment>
      {project && (
        <aside
          className={cn(
            "fixed",
            isMobile
              ? "left-0 h-[calc(var(--h-screen,100vh)_-_150px)] w-[var(--w-screen,100vw)]"
              : `${
                  document.dir === "rtl" ? "left-[0px]" : "left-[430px]"
                } h-full w-[calc(var(--w-screen,100vw)_-_430px)]`,
          )}
        >
          <MapViewEditor
            id="map-ideaMap"
            mapSettings={ideaMap?.mapSettings}
            setMapSettings={() => {}}
            shouldFlyTo={ideaMap?.mapSettings}
            accessibilityInfotext={accessibilityInfotext}
            allowFullscreen={false}
            fullscreenMap={false}
            mode="display"
            statefulMap={statefulMap}
            setStatefulMap={setStatefulMap}
            mapData={{
              ...ideaMap.map,
              gisData: gisDataArray as any,
              handleClickIdeaMarker: handle.clickIdeaMarker,
              pinData: ideaId && idea && idea,
              project,
              optimizeString: true,
            }}
          />
        </aside>
      )}

      <ScaleContainer
        transformOrigin={document.dir === "rtl" ? "100%" : "0"}
        state={
          ideaId || profileId || pathname.includes("addIdea") ? "scale" : "show"
        }
      >
        <IdeaMapList
          ideaMap={ideaMap}
          handle={handle}
          project={project}
          projectIdeas={projectIdeas}
          filteredProjectIdeas={filteredProjectIdeas}
          selectedArealFilter={selectedArealFilter}
          setSelectedArealFilter={setSelectedArealFilter}
          open={open}
          setOpen={setOpen}
          setPosition={setPosition}
          checkedSortOption={checkedSortOption}
          setCheckedSortOption={setCheckedSortOption}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          isMobile={isMobile}
          preview={preview}
        />
      </ScaleContainer>
    </React.Fragment>
  );
};

export default React.memo(IdeaMap);
